import TuneIcon from '@mui/icons-material/Tune';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import useIsMobile from 'hooks/useIsMobile';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MOBILE_FILTER_BAR_ML,
  MOBILE_FILTER_BAR_TOP,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import { isMetaDatePreset, type MetaDatePreset } from 'utils/metaDatePresets';

import ActiveFilterChips from './ActiveFilterChips';
import AdvancedFilterModal from './AdvancedFilterModal';
import DateRangePicker from './DateRangePicker';
import FilterDropdown from './FilterDropdown';
import MetaPeriodPicker from './MetaPeriodPicker';
import type { DateRangeValue, FilterFieldConfig, FilterOption, UseMasterFilterReturn } from './types';

interface MasterFilterProps extends UseMasterFilterReturn {
  dynamicOptions?: Record<string, FilterOption[]>;
  /** Label for the primary action button rendered at the end of the bar (e.g. "Aplicar", "Obtener métricas"). */
  actionLabel?: string;
  /** Alternate label shown after first data load (e.g. "Actualizar métricas"). */
  actionLabelUpdate?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  /** Shows a spinner on the action button and disables it. */
  actionLoading?: boolean;
  /** When true and actionLabelUpdate is provided, the button label switches to actionLabelUpdate. */
  hasData?: boolean;
  /** Override click handler for the "Filtros avanzados" button (e.g. to open a SmartFilterModal). */
  onAdvancedClick?: () => void;
  /** Number of active advanced filters (shown as badge count when onAdvancedClick is provided). */
  advancedFilterCount?: number;
  /** Active filter chips coming from an external source (e.g. SmartFilterModal). */
  externalChips?: import('./types').ActiveFilter[];
  /** Called when a chip from externalChips is removed. */
  onRemoveExternalChip?: (key: string) => void;
  /** Called when all external chips should be cleared. */
  onClearAllExternalChips?: () => void;
}

/** Shared label style — used above every filter control in the bar. Hidden on mobile. */
const LABEL_SX = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 400,
  fontSize: '0.7rem',
  color: `${NAVY}80`,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  lineHeight: 1,
  mb: 0.75,
  display: { xs: 'none', md: 'block' },
};

/** Input control sx (no label inside — label is above). */
const CONTROL_SX = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: RADIUS_ITEM,
    backgroundColor: '#ffffff',
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.875rem',
  },
} as const;

/** Wraps a control with the shared uppercase label above it. */
const FieldWrapper: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    {children}
  </Box>
);

const SEARCH_DEBOUNCE_MS = 1000;

/** Text input that debounces propagation so queries don't fire on every keystroke.
 *  Enter and blur flush immediately. */
const DebouncedTextControl: React.FC<{
  field: FilterFieldConfig;
  value: string;
  setValue: UseMasterFilterReturn['setValue'];
  onAction?: () => void;
}> = ({ field, value, setValue, onAction }) => {
  const [draft, setDraft] = useState(value);
  const lastCommittedRef = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value !== lastCommittedRef.current) {
      lastCommittedRef.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const commit = useCallback(
    (next: string) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (next === lastCommittedRef.current) return;
      lastCommittedRef.current = next;
      setValue(field.key, next);
    },
    [field.key, setValue]
  );

  return (
    <TextField
      size="small"
      type="search"
      name={`mf-${field.key}`}
      autoComplete="off"
      placeholder={field.placeholder ?? field.label}
      value={draft}
      onChange={(e) => {
        const next = e.target.value;
        setDraft(next);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => commit(next), SEARCH_DEBOUNCE_MS);
      }}
      onBlur={() => commit(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit(draft);
          if (onAction) onAction();
        }
      }}
      sx={{ minWidth: { xs: 0, md: 250 }, width: { xs: '100%', sm: 'auto' }, ...CONTROL_SX }}
    />
  );
};

/** Renders the bare control (no label) for each filter kind. */
const renderControl = (
  field: FilterFieldConfig,
  values: UseMasterFilterReturn['values'],
  setValue: UseMasterFilterReturn['setValue'],
  dynamicOptions?: Record<string, FilterOption[]>,
  onAction?: () => void
): React.ReactNode => {
  const val = values[field.key];

  switch (field.kind) {
    case 'dateRange': {
      const dr = (val as DateRangeValue | undefined) ?? { start: null, end: null };
      return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DateRangePicker
            value={dr}
            onChange={(v) => setValue(field.key, v)}
            disablePresets={field.disablePresets}
            maxRangeDays={field.maxRangeDays}
          />
        </LocalizationProvider>
      );
    }

    case 'metaDatePreset': {
      const preset = (isMetaDatePreset(val) ? val : (field.defaultValue as MetaDatePreset)) as MetaDatePreset;
      return <MetaPeriodPicker value={preset} onChange={(p) => setValue(field.key, p)} />;
    }

    case 'singleSelect': {
      const options = field.options ?? dynamicOptions?.[field.key] ?? [];
      return (
        <Select
          size="small"
          displayEmpty
          value={(val as string) ?? ''}
          onChange={(e) => setValue(field.key, e.target.value || null)}
          sx={{
            minWidth: { xs: 0, sm: 140 },
            width: { xs: '100%', sm: 'auto' },
            ...CONTROL_SX['& .MuiOutlinedInput-root'],
          }}
        >
          <MenuItem value="">
            <Typography sx={{ fontWeight: 400, fontSize: '0.875rem', color: 'text.secondary' }}>
              {field.label}: Todos
            </Typography>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{opt.label}</Typography>
            </MenuItem>
          ))}
        </Select>
      );
    }

    case 'multiSelect': {
      const options = field.options ?? dynamicOptions?.[field.key] ?? [];
      const selected = (val as string[] | undefined) ?? [];
      return (
        <FilterDropdown
          label={field.label}
          options={options}
          selectedValues={selected}
          onChange={(v) => setValue(field.key, v)}
        />
      );
    }

    case 'text': {
      return (
        <DebouncedTextControl field={field} value={(val as string) ?? ''} setValue={setValue} onAction={onAction} />
      );
    }

    default:
      return null;
  }
};

const MasterFilter: React.FC<MasterFilterProps> = ({
  config,
  values,
  setValue,
  clearOne,
  clearAll,
  activeFilters,
  modalOpen,
  openModal,
  closeModal,
  dynamicOptions,
  actionLabel,
  actionLabelUpdate,
  onAction,
  actionDisabled = false,
  actionLoading = false,
  hasData = false,
  onAdvancedClick,
  advancedFilterCount,
  externalChips = [],
  onRemoveExternalChip,
  onClearAllExternalChips,
}) => {
  const { isMobile } = useIsMobile();

  // On mobile, text and dateRange fields stay in the bar; others move to the modal
  const barFields = config.filter(
    (f) => f.placement === 'bar' && (!isMobile || f.kind === 'text' || f.kind === 'dateRange')
  );
  const modalFields = config.filter(
    (f) => f.placement === 'modal' || (isMobile && f.placement === 'bar' && f.kind !== 'text' && f.kind !== 'dateRange')
  );

  const activeModalCount =
    advancedFilterCount ?? activeFilters.filter((af) => modalFields.some((f) => f.key === af.key)).length;

  // On mobile, count active demoted bar filters (excludes dateRange which stays visible)
  const mobileExtraCount = isMobile
    ? activeFilters.filter((af) =>
        config.some((f) => f.key === af.key && f.placement === 'bar' && f.kind !== 'text' && f.kind !== 'dateRange')
      ).length
    : 0;

  const totalAdvancedCount = activeModalCount + mobileExtraCount;

  const hasAdvancedButton = onAdvancedClick || modalFields.length > 0;

  const displayLabel = hasData && actionLabelUpdate ? actionLabelUpdate : actionLabel;
  const isButtonDisabled = actionDisabled || actionLoading;

  return (
    <Box>
      {/* ── Filter zone (bar + chips grouped) ─────────────────── */}
      <Box
        sx={{
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: '0px 4px 16px rgba(21, 24, 58, 0.05)',
          border: `1px solid rgba(21, 24, 58, 0.07)`,
          position: { xs: 'fixed', md: 'static' },
          top: { xs: `${MOBILE_FILTER_BAR_TOP}px`, md: 'auto' },
          left: { xs: `${MOBILE_FILTER_BAR_ML}px`, md: 'auto' },
          right: { xs: 0, md: 'auto' },
          zIndex: { xs: 99, md: 'auto' },
        }}
      >
        {/* ── Filter bar ──────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'center', md: 'flex-end' },
            flexDirection: 'row',
            gap: { xs: 1, sm: 1.5 },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            overflowX: { xs: 'auto', md: 'visible' },
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1, sm: 1.25 },
          }}
        >
          {barFields.map((field) => {
            if (field.visibleWhen && !field.visibleWhen(values)) return null;
            return (
              <Box key={field.key} sx={{ flex: { xs: field.kind === 'text' ? 1 : 'none', sm: 'none' }, minWidth: 0 }}>
                <FieldWrapper label={field.label}>
                  {renderControl(field, values, setValue, dynamicOptions, onAction)}
                </FieldWrapper>
              </Box>
            );
          })}

          {/* Primary action button */}
          {displayLabel && onAction && (
            <Button
              onClick={onAction}
              disabled={isButtonDisabled}
              startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: NAVY }} /> : undefined}
              sx={{
                height: 40,
                borderRadius: RADIUS_COIN,
                color: NAVY,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 700,
                fontSize: '0.875rem',
                px: 2.5,
                backgroundColor: YELLOW,
                '&:hover': { backgroundColor: `${YELLOW}cc` },
                '&.Mui-disabled': { backgroundColor: `${NAVY}14`, color: `${NAVY}60` },
              }}
            >
              {displayLabel}
            </Button>
          )}

          {/* Advanced filters button */}
          {hasAdvancedButton && (
            <Button
              onClick={onAdvancedClick ?? openModal}
              sx={{
                height: 40,
                borderRadius: RADIUS_COIN,
                color: totalAdvancedCount > 0 ? '#fff' : NAVY,
                textTransform: 'none',
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                fontSize: '0.875rem',
                px: { xs: 1, md: 2.5 },
                minWidth: { xs: 40, md: 0 },
                ml: 'auto',
                backgroundColor: totalAdvancedCount > 0 ? NAVY : SURFACE_CONTAINER_LOWEST,
                border: totalAdvancedCount > 0 ? 'none' : `1px solid rgba(21, 24, 58, 0.12)`,
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: totalAdvancedCount > 0 ? `${NAVY}dd` : `${NAVY}08`,
                  border: totalAdvancedCount > 0 ? 'none' : `1px solid rgba(21, 24, 58, 0.35)`,
                },
              }}
            >
              <TuneIcon sx={{ fontSize: '1.1rem', mr: { xs: 0, md: 0.75 } }} />
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                Filtros avanzados
              </Box>
              {totalAdvancedCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    backgroundColor: '#fff',
                    color: NAVY,
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 800,
                    lineHeight: 1,
                  }}
                >
                  {totalAdvancedCount}
                </Box>
              )}
            </Button>
          )}
        </Box>

        {/* ── Active filter chips (inside shared zone) ────────── */}
        <ActiveFilterChips
          activeFilters={[...activeFilters, ...externalChips]}
          onRemove={(key) => {
            if (externalChips.some((c) => c.key === key)) {
              onRemoveExternalChip?.(key);
            } else {
              clearOne(key);
            }
          }}
          onClearAll={() => {
            clearAll();
            onClearAllExternalChips?.();
          }}
        />
      </Box>

      {/* ── Advanced filter modal ────────────────────────────────── */}
      <AdvancedFilterModal
        open={modalOpen}
        onClose={closeModal}
        fields={modalFields}
        values={values}
        onApply={setValue}
        dynamicOptions={dynamicOptions}
      />
    </Box>
  );
};

export default MasterFilter;

export type {
  DateRangeValue,
  FilterFieldConfig,
  FilterOption,
  MasterFilterPersistence,
  MasterFilterValues,
  UseMasterFilterReturn,
} from './types';
export { useMasterFilter } from './useMasterFilter';
